import { useContext, useEffect, useMemo, useState } from 'react';
import { MaterialReactTable } from 'material-react-table';
import { MRT_Localization_PT_BR } from 'material-react-table/locales/pt-BR';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    ThemeProvider,
    Tooltip,
} from '@mui/material';
import { Business, Key, LockOpen, PowerSettingsNew } from '@mui/icons-material';

import { Context } from '../../../context/UserContext';
import CrudsAPI from '../../../api/CRUDs';
import { useNavigateParams } from '../../../hooks/useNavigateParams';
import useFlashMessage from '../../../hooks/useFlashMessage';
import { obterPermissoesUsuario } from '../../../utils/funcoes';
import tableThemeCustom from '../../form/tableThemeCustom';
import CredencialGeradaDialog from './CredencialGeradaDialog';

import { useMRTState } from '../../../hooks/useMRTState';
import { getMRTDefaultProps } from '../../../helpers/mrt/getMRTDefaultProps';
import { podeIncluir, podeEditar, podeCancelar } from '../../../hooks/usePermisionMRT';

import { getEmpresaScope } from '../../../helpers/sessionEmpresa';

function formatarDataHora(value) {
    if (!value) return 'Nunca';

    const data = new Date(value);
    return Number.isNaN(data.getTime()) ? '' : data.toLocaleString('pt-BR');
}

function Index() {
    const { parametro } = useContext(Context);
    const { pessoaId, empresaId, empresaIds } = getEmpresaScope();
    const { setFlashMessage } = useFlashMessage();
    const navigate = useNavigateParams();
    const urlParams = new URLSearchParams(window.location.search);
    const ul = urlParams.get('ul') || 1;
    const li = urlParams.get('li') || 109;
    const [token] = useState(localStorage.getItem('token') || '');

    const mrt = useMRTState({
        storageKey: 'equipe.index',
        initialState: {
            columnFilterFns: {},
            columnFilters: [],
            globalFilter: '',
            sorting: [],
            pagination: {
                pageIndex: 0,
                pageSize: parametro?.paginacao || 10,
            },
        },
    });

    const {
        columnFilterFns,
        setColumnFiltersMode,
        setColumnFilterFns,
        columnFilters,
        setColumnFilters,
        globalFilter,
        setGlobalFilter,
        sorting,
        setSorting,
        pagination,
        setPagination,
        isLoading,
        setIsLoading,
        isError,
        setIsError,
        isRefetching,
        setIsRefetching,
        rowCount,
        setRowCount,
    } = mrt;
    const mrtDefault = getMRTDefaultProps({
        mrt,
        parametro,
    });

    const [dados, setDados] = useState([]);

    const [acao, setAcao] = useState(null);
    const [credencialGerada, setCredencialGerada] = useState(null);

    async function fetchData() {
        dados.length ? setIsRefetching(true) : setIsLoading(true);

        try {
            const response = await CrudsAPI.integracaoCliente.listar(
                pagination.pageIndex * pagination.pageSize,
                pagination.pageSize,
                JSON.stringify(columnFilters),
                JSON.stringify(columnFilterFns),
                globalFilter || '',
                JSON.stringify(sorting)
            );

            setDados(response.data.data);
            setRowCount(response.data.meta.totalRowCount);
            setIsError(false);
        } catch (error) {
            setIsError(true);
            setFlashMessage(
                error.response?.data?.message || 'Não foi possível consultar as credenciais.',
                error.response?.data?.type || 'error'
            );
        } finally {
            setIsLoading(false);
            setIsRefetching(false);
        }
    }

    useEffect(() => {
        if (!token) {
            navigate('/Auth', { replace: true });
            return;
        }

        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, columnFilterFns, columnFilters, globalFilter, pagination.pageIndex, pagination.pageSize, sorting]);

    useEffect(() => {
        if (!pessoaId || !empresaId || !ul || !li) return;
        //funcao para setar localStorage
        localStorage.setItem(
            'pagination',
            JSON.stringify({
                pageIndex: pagination.pageIndex,
                pageSize: pagination.pageSize,
            })
        );
        localStorage.setItem('columnFilterFns', JSON.stringify(columnFilterFns));
        localStorage.setItem('columnFilters', JSON.stringify(columnFilters));
        localStorage.setItem('globalFilter', JSON.stringify(globalFilter));
        localStorage.setItem('sorting', JSON.stringify(sorting));
        obterPermissoesUsuario(pessoaId, empresaId, ul, li);
    }, [pessoaId, empresaId, ul, li]);

    async function confirmarAcao() {
        if (!acao?.cliente) return;

        try {
            const response =
                acao.tipo === 'rotacionar'
                    ? await CrudsAPI.integracaoCliente.rotacionar({
                          uuid: acao.cliente.uuid,
                      })
                    : await CrudsAPI.integracaoCliente.alterarStatus({
                          uuid: acao.cliente.uuid,
                          ativo: !acao.cliente.ativo,
                      });

            setAcao(null);
            setFlashMessage(response.data.message, response.data.type);

            if (acao.tipo === 'rotacionar') {
                setCredencialGerada(response.data.data);
            }

            await fetchData();
        } catch (error) {
            setFlashMessage(
                error.response?.data?.message || 'Não foi possível executar a operação.',
                error.response?.data?.type || 'error'
            );
        }
    }

    const columns = useMemo(
        () => [
            {
                id: 'Credenciais',
                header: 'CREDENCIAIS DE INTEGRAÇÃO PDV',
                columns: [
                    { accessorKey: 'Empresa.nomeFantasia', header: 'Empresa' },
                    { accessorKey: 'codigo', header: 'Código' },
                    { accessorKey: 'descricao', header: 'Descrição' },
                    { accessorKey: 'clientId', header: 'clientId' },
                    {
                        accessorKey: 'ativo',
                        header: 'Ativo',
                        Cell: ({ cell }) => (cell.getValue() ? 'Sim' : 'Não'),
                    },
                    {
                        accessorKey: 'ultimoAcessoEm',
                        header: 'Último acesso',
                        Cell: ({ cell }) => formatarDataHora(cell.getValue()),
                    },
                ],
            },
        ],
        []
    );

    const theme = tableThemeCustom();
    const tituloAcao =
        acao?.tipo === 'rotacionar'
            ? 'Rotacionar credencial?'
            : acao?.cliente?.ativo
              ? 'Revogar credencial?'
              : 'Ativar credencial?';
    const textoAcao =
        acao?.tipo === 'rotacionar'
            ? 'O clientSecret atual deixará de gerar novos tokens. O novo segredo será mostrado uma única vez.'
            : acao?.cliente?.ativo
              ? 'O cliente deixará de acessar a integração imediatamente.'
              : 'O cliente voltará a poder obter tokens e consumir a integração.';

    return (
        <div>
            <ThemeProvider theme={theme}>
                <MaterialReactTable
                    {...mrtDefault}
                    columns={columns}
                    data={dados}
                    getRowId={(row) => row.uuid}
                    renderTopToolbarCustomActions={() => (
                        <Button
                            color="secondary"
                            variant="contained"
                            disabled={!podeIncluir()}
                            onClick={() => navigate('/IntegracaoClientes/CreateOrEdit', urlParams)}
                        >
                            Novo
                        </Button>
                    )}
                    renderRowActions={({ row }) => (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Tooltip title="Editar configurações">
                                <span>
                                    <IconButton
                                        color="secondary"
                                        disabled={!podeEditar()}
                                        onClick={() => {
                                            const params = new URLSearchParams(urlParams);
                                            params.set('uuid', row.original.uuid);
                                            navigate('/IntegracaoClientes/CreateOrEdit', params);
                                        }}
                                    >
                                        <Business />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Tooltip title="Rotacionar clientSecret">
                                <span>
                                    <IconButton
                                        color="primary"
                                        disabled={!row.original.ativo || !podeEditar()}
                                        onClick={() => setAcao({ tipo: 'rotacionar', cliente: row.original })}
                                    >
                                        <Key />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Tooltip title={row.original.ativo ? 'Revogar acesso' : 'Ativar acesso'}>
                                <span>
                                    <IconButton
                                        color={row.original.ativo ? 'error' : 'success'}
                                        disabled={!podeCancelar()}
                                        onClick={() => setAcao({ tipo: 'status', cliente: row.original })}
                                    >
                                        {row.original.ativo ? <PowerSettingsNew /> : <LockOpen />}
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Box>
                    )}
                />
            </ThemeProvider>

            <Dialog open={Boolean(acao)} onClose={() => setAcao(null)}>
                <DialogTitle>{tituloAcao}</DialogTitle>
                <DialogContent>
                    <DialogContentText>{textoAcao}</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAcao(null)}>Cancelar</Button>
                    <Button variant="contained" color="warning" onClick={confirmarAcao}>
                        Confirmar
                    </Button>
                </DialogActions>
            </Dialog>

            <CredencialGeradaDialog
                open={Boolean(credencialGerada)}
                credencial={credencialGerada}
                onClose={() => setCredencialGerada(null)}
            />
        </div>
    );
}

export default Index;
